
import { User } from 'models/User';
import { tokenStorage } from '../utils/tokenStorage';
import { httpClient } from '../utils/httpClient';

// ============================================================
// authService.ts
// ============================================================

const API_BASE = process.env.REACT_APP_API_URL ?? '';

export interface LoginCredentials {
  identifier: string;
  password: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T | null;
  timestamp: string;
}

export interface LoginData {
  token:             string;
  refreshToken:      string;
  tipoToken:         string;
  expiracionMs:      number;
  idUsuario:         number;
  username:          string;
  nombres:           string;
  apellidos:         string;
  email:             string;
  dni:               string;
  telefono:          string;
  fotoPerfil:        string | null;
  rol:               string;
  ultimoAcceso:      string;
  passwordExpirada?: boolean;
}

export interface LoginResult {
  user:             User;
  passwordExpirada: boolean;
}

export const AuthService = {

  async login(credentials: LoginCredentials): Promise<LoginResult> {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'X-Client-Type': 'WEB',
      },
      body:    JSON.stringify(credentials),
    }).catch((err: unknown) => {
      if (err instanceof TypeError) {
        throw new Error(
          'No se pudo conectar con el servidor. ' +
          'Verifica tu conexión a internet e inténtalo nuevamente.',
        );
      }
      throw err;
    });

    const body: ApiResponse<LoginData> = await res.json();

    if (!body.success || !body.data) {
      throw new Error(body.message ?? 'Error al iniciar sesión');
    }

    const { token, refreshToken, expiracionMs, passwordExpirada, ...rest } = body.data;

    tokenStorage.saveAccessToken(token, expiracionMs);
    tokenStorage.saveRefreshToken(refreshToken);
    tokenStorage.savePasswordExpired(passwordExpirada ?? false);

    const user = User.fromApiResponse({ token, expiracionMs, ...rest } as Record<string, unknown>);
    tokenStorage.saveUser(user);

    return { user, passwordExpirada: passwordExpirada ?? false };
  },

  async logout(): Promise<void> {
    const refreshToken = tokenStorage.getRefreshToken();

    // Invalidar refresh token en el backend
    if (refreshToken) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'X-Client-Type': 'WEB',
            'Authorization': `Bearer ${tokenStorage.getAccessToken() ?? ''}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Si falla la petición igual limpiamos localmente
      }
    }

    tokenStorage.clearAll();
  },

  getStoredSession(): User | null {
    const data = tokenStorage.getUser<Record<string, unknown>>();
    if (!data) return null;
    try {
      // Inyectar el access token actualizado
      const accessToken = tokenStorage.getAccessToken();
      return User.fromApiResponse({ ...data, token: accessToken ?? '' });
    } catch {
      return null;
    }
  },

  isSessionValid(): boolean {
    const hasRefresh = !!tokenStorage.getRefreshToken();
    const hasUser    = !!tokenStorage.getUser();
    // La sesión es válida si tiene refresh token (el access se puede renovar)
    return hasRefresh && hasUser;
  },

  /** PUT /auth/change-password — requiere token activo */
  async changePassword(payload: {
    passwordActual:       string;
    passwordNueva:        string;
    passwordConfirmacion: string;
  }): Promise<void> {
    return httpClient.put<void>('/auth/change-password', payload);
  },
};